import {
  buildCoachAvailabilityQuery,
  CoacheeElementTokenProvider,
  CronofyDateTimePicker,
} from '@efdigital/ll-addon-coaching';
import { Heading } from '@efdigital/ll-addon-ui';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import { CoachProfileHeader } from '@efdigital/ll-xceed-v2/components/coaching/coach/coach-profile-header.gts';
import type { CoachingCoacheeCohortSessionsBookChemistryModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/sessions/book-session';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  DateTimePickerAction,
  DateTimePickerConfig,
} from 'cronofy-elements';
import { pageTitle } from 'ember-page-title';

interface BookSessionSignature {
  Args: {
    model: CoachingCoacheeCohortSessionsBookChemistryModel;
    controller: unknown;
  };
}

const dateTimePickerConfig: DateTimePickerConfig = {
  logs: 'info',
};

export default class BookSessionTemplate extends Component<BookSessionSignature> {
  @service declare router: RouterService;
  @tracked saving = false;

  dateTimePickerAction = async (action: DateTimePickerAction) => {
    if (action.notification.type === 'slot_selected') {
      const idempotencyKey = crypto.randomUUID();
      this.saving = true;
      try {
        const { start, end } = action.notification.slot;
        await this.args.model.planner.bookSession(
          this.args.model.coach.user,
          'coaching',
          idempotencyKey,
          start,
          end
        );
        this.router.transitionTo(
          'coaching.coachee.cohort.sessions',
          this.args.model.program_id,
          this.args.model.cohort_id
        );
      } finally {
        this.saving = false;
      }
    }
  };

  <template>
    {{pageTitle "Book coaching session"}}

    <div class="container mb-3">
      <CoachProfileHeader
        @coach={{@model.coach}}
        @partnershipStatus={{@model.planner.getStatusForCoach
          @model.coach.user
        }}
        class="mb-5"
      />

      {{#if (@model.planner.canBookCoachingSession @model.coach.user)}}
        <Heading
          @size="sm"
          @text="Book coaching session"
          @secondary="Select a time and date for your coaching session."
        />
        <div class="card rounded-4 shadow-sm border p-3 p-lg-4">
          <div class="card-body">
            {{#if this.saving}}
              <Spinner />
            {{else}}
              <CoacheeElementTokenProvider
                @coachId={{@model.coach.user}}
                as |cronofyContext|
              >
                {{#if cronofyContext.accountStatus.accountSub}}
                  <CronofyDateTimePicker
                    @cronofyContext={{cronofyContext}}
                    @availabilityQuery={{buildCoachAvailabilityQuery
                      60
                      cronofyContext.accountStatus.accountSub
                    }}
                    @onAction={{this.dateTimePickerAction}}
                    @elementConfig={{dateTimePickerConfig}}
                    class="mb-5 d-flex flex-row justify-content-center"
                  />
                {{else}}
                  <div class="alert alert-danger">
                    <p class="mb-0">You cannot book a coaching session with this
                      coach because they have not connected their calendar to
                      the system.</p>
                  </div>
                {{/if}}
              </CoacheeElementTokenProvider>
            {{/if}}
          </div>
        </div>
      {{else}}
        <div class="alert alert-danger">
          <p class="mb-0">Sorry, you cannot book a coaching session with this
            coach.</p>
        </div>
      {{/if}}
    </div>
  </template>
}
